/** Union of two spans (the smaller start, the larger end). */
function union(a, b) {
    if (a && b) {
        return { start: Math.min(a.start, b.start), end: Math.max(a.end, b.end) };
    }
    return a ?? b;
}
/**
 * Best-effort source span of any node. The parser populates `range` on every
 * node it builds, so this normally returns that directly; the recursive
 * fallback covers hand-built AST (tests) and degrades gracefully if a future
 * node type forgets to set its range.
 */
export function nodeRange(node) {
    switch (node.type) {
        case "Literal":
            return node.range;
        case "VariableAccess":
            return node.range;
        case "FunctionCall":
            return (node.range ??
                node.arguments.reduce((acc, arg) => union(acc, nodeRange(arg)), node.nameRange));
        case "MemberAccess":
            return node.range ?? union(nodeRange(node.object), node.propertyRange);
        case "BinaryOperation":
        case "Comparison":
        case "Logical":
            return node.range ?? union(nodeRange(node.left), nodeRange(node.right));
        case "UnaryOperation":
        case "Not":
            return node.range ?? nodeRange(node.operand);
    }
}
/** Direct child nodes of a node, for depth-first descent. */
function children(node) {
    switch (node.type) {
        case "Literal":
        case "VariableAccess":
            return [];
        case "FunctionCall":
            return node.arguments;
        case "MemberAccess":
            return [node.object];
        case "BinaryOperation":
        case "Comparison":
        case "Logical":
            return [node.left, node.right];
        case "UnaryOperation":
        case "Not":
            return [node.operand];
    }
}
/**
 * The smallest node whose source span contains `offset`. Used by the editor to
 * resolve "which sub-expression is the cursor on" for value inspection. Returns
 * null when no node with a known span covers the offset.
 */
export function findNodeAtOffset(ast, offset) {
    let best = null;
    let bestSize = Infinity;
    const visit = (node) => {
        const range = nodeRange(node);
        if (range && offset >= range.start && offset <= range.end) {
            const size = range.end - range.start;
            if (size <= bestSize) {
                best = node;
                bestSize = size;
            }
        }
        for (const child of children(node))
            visit(child);
    };
    visit(ast);
    return best;
}
//# sourceMappingURL=ast-navigation.js.map